import React, { useState, useMemo } from 'react';
import Donut from './Donut';
import { fmt$, signed$, pnlClr } from './fmt';
import { useTheme } from '../../../context/ThemeContext';

// Combined allocation + holdings view: one panel with a Security | Bucket | Sector
// toggle. Security and Sector render as data tables (Market Value · % · P&L),
// Bucket renders the nested class → portfolio → strategy tree; a donut sits on the
// right, and the book-wide P&L reconciliation strip anchors the bottom. This
// absorbs the old standalone Top Holdings card — same data, no duplication.
const COLORS = ['#c9a96e', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16'];
const OTHERS = '#6b7280';

// Allocation bar + % label — gold proportion bar (width = pct of AUM, min 2% so
// small holdings still show a sliver) with the number right-aligned beside it.
// Shared across the Security / Sector / Bucket tables.
const Bar = ({ pct }) => {
  const p = pct || 0;
  return (
    <div className="flex items-center gap-2">
      <span className="flex-1 h-[6px] rounded-full bg-gray-100 dark:bg-[#20272f] overflow-hidden">
        <span className="block h-full rounded-full" style={{ width: `${Math.max(p > 0 ? 2 : 0, Math.min(100, p))}%`, background: 'var(--brand-gold)' }} />
      </span>
      <span className="w-11 text-right tabular-nums text-gray-500 shrink-0">{p.toFixed(1)}%</span>
    </div>
  );
};

// Collapse [{label, value}] into top-N (+ Others), colored, DESC by value — for the donut.
function topN(items, n = 6, colors = COLORS) {
  const clean = (items || []).filter((x) => x.value > 0).sort((a, b) => b.value - a.value);
  const head = clean.slice(0, n).map((x, i) => ({ ...x, color: colors[i % colors.length] }));
  const tail = clean.slice(n);
  if (tail.length) head.push({ label: `Others (${tail.length})`, value: tail.reduce((s, x) => s + x.value, 0), color: OTHERS });
  return head;
}

const MODES = [
  { id: 'security', label: 'Security', center: 'Deployed' },
  { id: 'bucket', label: 'Bucket', center: 'Buckets' },
  { id: 'sector', label: 'Sector', center: 'Sectors' },
];
const ROW_CAP = 12; // show a few more than the old top-10, capped so the panel stays compact


export default function AllocationBlock({ tree, sectors, loading }) {
  const [mode, setMode] = useState('security');
  const [open, setOpen] = useState({});
  const { isDark } = useTheme();
  // theme accent leads the palette: gold (dark) / cobalt (light)
  const palette = useMemo(() => [isDark ? '#c9a96e' : '#3b82f6', ...COLORS.slice(1)], [isDark]);

  const holdings = tree?.total_holdings || 0;
  const pctOf = (h) => (holdings > 0 ? (h / holdings) * 100 : 0);
  const buckets = tree?.buckets || [];
  const bySymbol = tree?.by_symbol || [];
  const book = tree?.book_pnl;
  const toggle = (k) => setOpen((e) => ({ ...e, [k]: !e[k] }));

  const donutSegs = useMemo(() => {
    if (mode === 'security') return topN(bySymbol.map((s) => ({ label: s.symbol, value: (s.pct || 0) * 100 })), 6, palette);
    if (mode === 'bucket') return topN(buckets.map((b) => ({ label: b.strategy_class, value: pctOf(b.holdings_usd) })), 6, palette);
    return topN((sectors || []).map((s) => ({ label: s.sector, value: (s.pct || 0) * 100 })), 6, palette);
  }, [mode, bySymbol, buckets, sectors, palette]); // eslint-disable-line react-hooks/exhaustive-deps

  const meta = MODES.find((m) => m.id === mode);
  const sub = mode === 'security'
    ? `By security · top ${Math.min(ROW_CAP, bySymbol.length)} of ${bySymbol.length}`
    : mode === 'bucket' ? 'By bucket · class → portfolio → strategy' : 'By GICS sector · strategy attribution';

  const Breakdown = () => {
    if (loading && !tree) return (
      <div className="space-y-2.5 py-2">
        {[70, 52, 60, 44, 38].map((wpct, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="h-3.5 skeleton" style={{ width: 44 }} />
            <div className="h-3.5 skeleton flex-1" style={{ maxWidth: `${wpct}%` }} />
          </div>
        ))}
      </div>
    );

    if (mode === 'bucket') {
      if (!buckets.length) return <div className="text-xs text-gray-400 italic py-4">No strategies deployed.</div>;
      return (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs" style={{ tableLayout: 'fixed' }}>
            <colgroup><col style={{ width: '44%' }} /><col style={{ width: '32%' }} /><col style={{ width: '24%' }} /></colgroup>
            <thead>
              <tr className="text-[9px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
                <th className="text-left py-2">Strategy</th><th className="text-left py-2">Allocation</th><th className="text-right py-2">Holdings</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b, bi) => {
                const bOpen = !!open[b.strategy_class];
                return (
                  <React.Fragment key={b.strategy_class}>
                    <tr className="border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-[#1c2330] cursor-pointer" onClick={() => toggle(b.strategy_class)}>
                      <td className="py-2 text-gray-700 dark:text-gray-200 truncate">
                        <span className={`inline-block w-3 text-gray-400 text-[10px] transition-transform ${bOpen ? 'rotate-90' : ''}`}>▸</span>
                        <span className="inline-block w-2 h-2 rounded-full mx-1.5 align-middle" style={{ background: palette[bi % palette.length] }} />
                        {b.strategy_class}<span className="ml-1.5 text-[10px] text-gray-400">{b.n_filled}/{b.n_strategies}</span>
                      </td>
                      <td className="py-2 pr-3"><Bar pct={pctOf(b.holdings_usd)} /></td>
                      <td className="py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmt$(b.holdings_usd)}</td>
                    </tr>
                    {bOpen && (b.portfolios || []).map((p) => {
                      const pk = `${b.strategy_class}::${p.portfolio_id}`;
                      const pOpen = !!open[pk];
                      return (
                        <React.Fragment key={pk}>
                          <tr className="border-b border-gray-100 dark:border-gray-800/40 hover:bg-gray-50 dark:hover:bg-[#1c2330] cursor-pointer" onClick={() => toggle(pk)}>
                            <td className="py-1.5 pl-5 text-gray-500 dark:text-gray-400 truncate" title={p.name}>
                              <span className={`inline-block w-3 text-gray-400 text-[10px] transition-transform ${pOpen ? 'rotate-90' : ''}`}>▸</span> {p.name}
                              <span className="ml-1.5 text-[10px] text-gray-400">{p.n_filled}/{p.n_strategies}</span>
                            </td>
                            <td className="py-1.5 pr-3"><Bar pct={pctOf(p.holdings_usd)} /></td>
                            <td className="py-1.5 text-right tabular-nums text-gray-500">{fmt$(p.holdings_usd)}</td>
                          </tr>
                          {pOpen && (p.strategies || []).map((s) => (
                            <tr key={s.id} className={`border-b border-gray-100 dark:border-gray-800/30 ${s.is_active ? '' : 'opacity-50'}`}>
                              <td className="py-1 pl-10 text-gray-500 dark:text-gray-400 truncate" title={s.name}>{s.symbol}{!s.is_active && <span className="ml-1.5 text-[9px] uppercase">off</span>}</td>
                              <td className="py-1 pr-3"><Bar pct={(s.pct || 0) * 100} /></td>
                              <td className="py-1 text-right tabular-nums text-gray-500">{fmt$(s.holdings_usd)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    if (mode === 'sector') {
      const rows = sectors || [];
      if (!rows.length) return <div className="text-xs text-gray-400 italic py-4">No sector data yet — holdings need a sector mapping.</div>;
      return (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs" style={{ tableLayout: 'fixed' }}>
            <colgroup><col style={{ width: '40%' }} /><col style={{ width: '34%' }} /><col style={{ width: '26%' }} /></colgroup>
            <thead>
              <tr className="text-[9px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
                <th className="text-left py-2">Sector</th><th className="text-left py-2">Allocation</th><th className="text-right py-2">Market Value</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s, i) => (
                <tr key={s.sector} className="border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-[#1c2330]">
                  <td className="py-2 text-gray-700 dark:text-gray-300 truncate">
                    <span className="inline-block w-2 h-2 rounded-sm mr-2 align-middle" style={{ background: palette[i % palette.length] }} />{s.sector}
                  </td>
                  <td className="py-2 pr-3"><Bar pct={(s.pct || 0) * 100} /></td>
                  <td className="py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmt$(s.market_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // security — the merged holdings table. Layout: Symbol (tight) · Allocation
    // (bar + %, right after the ticker) · Market Value · Total P&L.
    const rows = bySymbol.slice(0, ROW_CAP);
    if (!rows.length) return <div className="text-xs text-gray-400 italic py-4">No holdings yet — the book is in cash.</div>;
    return (
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs" style={{ tableLayout: 'fixed' }}>
          <colgroup><col style={{ width: '15%' }} /><col style={{ width: '37%' }} /><col style={{ width: '23%' }} /><col style={{ width: '25%' }} /></colgroup>
          <thead>
            <tr className="text-[9px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
              <th className="text-left py-2">Symbol</th><th className="text-left py-2">Allocation</th><th className="text-right py-2">Market Value</th><th className="text-right py-2">Total P&amp;L</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => {
              const p = (h.pct || 0) * 100;
              return (
                <tr key={h.symbol} className="border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-[#1c2330]">
                  <td className="py-2 font-semibold text-gray-800 dark:text-gray-200 truncate">{h.symbol}</td>
                  <td className="py-2 pr-3"><Bar pct={p} /></td>
                  <td className="py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmt$(h.holdings_usd)}</td>
                  <td className={`py-2 text-right tabular-nums ${pnlClr(h.pnl)}`} title={`Realized ${signed$(h.realized)} · Unrealized ${signed$(h.unrealized)}`}>
                    {h.pnl != null && h.pnl !== 0 ? signed$(h.pnl) : (h.pnl === 0 ? '$0' : '—')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="text-sm font-serif text-gray-800 dark:text-gray-200">Allocation &amp; Holdings</h3>
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-[#0d1117]">
          {MODES.map((m) => (
            <button key={m.id} onClick={() => setMode(m.id)}
              className={`px-3 py-1 text-[11.5px] rounded-md transition-colors ${mode === m.id ? 'bg-brand-gold text-[#14171d] font-semibold' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}>
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div className="text-[10.5px] uppercase tracking-wide text-gray-400 mb-3">{sub}</div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-4 items-stretch">
        <Breakdown />
        <div className="flex items-center justify-center">
          {donutSegs.length === 0
            ? <div className="text-gray-400 italic text-xs py-6">No data to chart</div>
            : <Donut segments={donutSegs} centerLabel={meta.center} size={220} />}
        </div>
      </div>

      {book && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[11px]">
          <span className="text-gray-400 uppercase tracking-wide">Book P&amp;L · all names</span>
          <span className="tabular-nums text-gray-500">
            Realized <span className={pnlClr(book.realized)}>{signed$(book.realized)}</span>
            {' + '}Unrealized <span className={pnlClr(book.unrealized)}>{signed$(book.unrealized)}</span>
            {' = '}<span className={`font-semibold ${pnlClr(book.total)}`}>{signed$(book.total)}</span>
          </span>
          {(book.n_exited_with_realized || 0) > 0 && (
            <span className="w-full text-[10px] text-gray-400 italic">
              Includes realized P&amp;L on {book.n_exited_with_realized} name{book.n_exited_with_realized === 1 ? '' : 's'} already exited (not held now).
            </span>
          )}
        </div>
      )}
    </div>
  );
}
